import React, { Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Expand } from "@sparefoot/react-commons";
import { Button } from "components/core/Button";
import { Link } from "components/core/Link";
import { Chevron } from "components/core/Icons";

import "./HeaderMenu.scss";

// Submenu parent items are dropdown labels (not navigable links). We still
// mirror the classes that `Link` would apply for `<Link last color="blue" />`
// so styling remains consistent. If `Link`'s class generation changes, update
// this string to match.
const SUBMENU_PARENT_LABEL_CLASSNAME =
	"sf-link link-menu-header last sf-type color-blue";

function CollapsibleSection({ title, links, defaultExpanded, menuExpanded }) {
	const [sectionExpanded, setSectionExpanded] = useState(defaultExpanded);

	useEffect(() => {
		if (!menuExpanded) {
			setSectionExpanded(defaultExpanded);
		}
	}, [menuExpanded, defaultExpanded]);

	return (
		<div className="collapsible-section">
			<button
				type="button"
				title={`${sectionExpanded ? "Collapse" : "Expand"} ${title}`}
				aria-expanded={sectionExpanded}
				onClick={() => setSectionExpanded(!sectionExpanded)}
				className={classnames("section-header", {
					"section-expanded": sectionExpanded,
					"section-collapsed": !sectionExpanded
				})}
			>
				{title}
				<Chevron rotate={sectionExpanded ? "up" : "down"} />
			</button>
			<Expand expanded={sectionExpanded} autoHeight>
				<div className="section-links">
					{links.map(({ href, text }) => (
						<Link
							last
							color="lightBlue"
							className="link-section-item"
							href={href}
							key={text}
						>
							{text}
						</Link>
					))}
				</div>
			</Expand>
		</div>
	);
}

CollapsibleSection.propTypes = {
	title: PropTypes.string.isRequired,
	links: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string.isRequired,
			href: PropTypes.string.isRequired
		})
	).isRequired,
	defaultExpanded: PropTypes.bool,
	menuExpanded: PropTypes.bool
};

CollapsibleSection.defaultProps = {
	defaultExpanded: true
};

function HeaderMenu({
	menuName,
	links,
	sections,
	footerLink,
	className,
	openMenuName,
	setOpenMenuName,
	isStacked
}) {
	const [expanded, setExpanded] = useState(false);

	const handleExpand = () => {
		if (expanded) {
			setExpanded(false);
			setOpenMenuName(null);
		} else {
			setExpanded(true);
			setOpenMenuName(menuName);
		}
	};

	useEffect(() => {
		if (!openMenuName || menuName !== openMenuName) {
			setExpanded(false);
		}
	}, [openMenuName, menuName]);

	const renderLinks = () =>
		links?.map(({ href, text, submenu }) => (
			<Fragment key={`fragment-${text}`}>
				{submenu ? (
					<span className={SUBMENU_PARENT_LABEL_CLASSNAME}>
						{text}
					</span>
				) : (
					<Link last className="link-menu" href={href}>
						{text}
					</Link>
				)}
				{submenu &&
					submenu.map((sub) => (
						<Link
							last
							color="lightBlue"
							className="link-submenu"
							href={sub.href}
							key={sub.text}
						>
							{sub.text}
						</Link>
					))}
			</Fragment>
		));

	const renderSections = () =>
		sections.map((section) => (
			<CollapsibleSection
				key={section.title}
				title={section.title}
				links={section.links}
				defaultExpanded={section.defaultExpanded}
				menuExpanded={expanded}
			/>
		));

	return (
		<div
			className={classnames(
				"header-menu",
				{ stacked: isStacked, "has-sections": !!sections },
				className
			)}
		>
			<Button
				title={`${expanded ? "Close" : "Open"} ${menuName} menu`}
				aria-expanded={expanded}
				onClick={handleExpand}
				className={classnames("button-menu", {
					"button-expanded": expanded,
					"button-collapsed": !expanded
				})}
				withIcon
			>
				{menuName}
				<Chevron rotate={expanded ? "up" : "down"} />
			</Button>
			<Expand expanded={expanded} autoHeight={!!sections}>
				<div>
					<div className="menu-content">
						{sections ? renderSections() : renderLinks()}
						{footerLink && (
							<Link
								last
								className="link-footer"
								href={footerLink.href}
							>
								{footerLink.text}
							</Link>
						)}
					</div>
				</div>
			</Expand>
		</div>
	);
}

HeaderMenu.propTypes = {
	isStacked: PropTypes.bool,
	menuName: PropTypes.string,
	links: PropTypes.arrayOf(PropTypes.object),
	sections: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			defaultExpanded: PropTypes.bool,
			links: PropTypes.arrayOf(
				PropTypes.shape({
					text: PropTypes.string.isRequired,
					href: PropTypes.string.isRequired
				})
			).isRequired
		})
	),
	footerLink: PropTypes.shape({
		text: PropTypes.string.isRequired,
		href: PropTypes.string.isRequired
	}),
	className: PropTypes.string,
	openMenuName: PropTypes.string,
	setOpenMenuName: PropTypes.func
};

export default HeaderMenu;
